import type { ReactNode } from 'react';
import { Target, Briefcase, TrendingUp, Trophy, Contact as ContactsIcon, FolderKanban, Users, CalendarOff, CalendarClock } from 'lucide-react';
import { WidgetContainer } from '../../infrastructure/widgets/WidgetContainer';
import { WidgetConfig } from '../../infrastructure/contracts/widget.contract';
import { useCompanyStats } from './hooks/useCompanyStats';

export const companyHealthConfig: WidgetConfig = {
  id: 'dash-company-health',
  title: 'Company Health',
  category: 'statistics',
  description: 'Company-wide snapshot',
  refreshStrategy: 'interval',
  refreshIntervalMs: 120_000,
  defaultSize: { w: 12, h: 3 },
  priority: 1,
  supportedRoles: ['superadmin'],
};

const num = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });

function Metric({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <div className="flex items-center gap-3 rounded-card-sm border border-outline bg-surface px-3 py-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-DEFAULT bg-primary-container text-on-primary-container">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="truncate text-label-caps uppercase tracking-wider text-on-surface-variant">{label}</p>
        <p className="text-headline-md font-semibold tabular-nums text-on-surface">{value}</p>
      </div>
    </div>
  );
}

export function CompanyHealthWidget() {
  const { data, isLoading, isError, error, refetch } = useCompanyStats();
  const o = data?.overview;
  const depts = data?.byDepartment ?? [];

  return (
    <WidgetContainer
      config={companyHealthConfig}
      isLoading={isLoading}
      isError={isError}
      errorMessage={error?.message}
      onRefresh={() => {
        refetch();
      }}
    >
      {!o ? (
        <p className="py-6 text-center text-body-sm italic text-on-surface-variant">No company data available.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Commercial */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Metric icon={<Target className="h-4 w-4" />} label="Open Leads" value={num.format(o.totalLeads)} />
            <Metric icon={<TrendingUp className="h-4 w-4" />} label="Leads Today" value={num.format(o.leadsToday)} />
            <Metric icon={<Briefcase className="h-4 w-4" />} label="Active Deals" value={num.format(o.dealsActive)} />
            <Metric icon={<TrendingUp className="h-4 w-4" />} label="Pipeline Value" value={num.format(o.dealsValue)} />
            <Metric icon={<Trophy className="h-4 w-4" />} label="Won Value" value={num.format(o.dealsWon)} />
            <Metric icon={<ContactsIcon className="h-4 w-4" />} label="Contacts" value={num.format(o.contacts)} />
          </div>

          {/* Delivery + Workforce */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Metric icon={<FolderKanban className="h-4 w-4" />} label="Active Projects" value={num.format(o.activeProjects)} />
            <Metric icon={<Users className="h-4 w-4" />} label="Active Staff" value={num.format(o.totalStaff)} />
            <Metric icon={<CalendarOff className="h-4 w-4" />} label="On Leave Today" value={num.format(o.onLeave)} />
            <Metric icon={<CalendarClock className="h-4 w-4" />} label="Pending Leaves" value={num.format(o.pendingLeaves)} />
          </div>

          {/* Department breakdown (verified counts only) */}
          {depts.length > 0 && (
            <div>
              <p className="mb-2 text-label-caps uppercase tracking-wider text-on-surface-variant/70">By Department</p>
              <div className="flex flex-wrap gap-2">
                {depts.map((d) => (
                  <span
                    key={d.department || 'unassigned'}
                    className="inline-flex items-center gap-1.5 rounded-full border border-outline bg-surface-variant px-3 py-1 text-data-md text-on-surface"
                  >
                    <span className="font-medium capitalize">{d.department || 'Unassigned'}</span>
                    <span className="tabular-nums text-on-surface-variant">
                      {d.active_count}/{d.total}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </WidgetContainer>
  );
}
